import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, LogIn, LogOut, MapPin, Pencil, Plus, Trash2, User, UserPlus } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useMenu, useMyOrders } from "../lib/api";
import { useI18n, statusLabel } from "../lib/i18n";
import { chf, fmtTime, statusTone } from "../lib/format";
import { Header } from "../components/Header";

const TONE = {
  warning: "bg-hm-terracotta/15 text-hm-terracotta",
  error: "bg-hm-error/10 text-hm-error",
  neutral: "bg-hm-tertiary text-hm-muted",
  success: "bg-hm-olive/15 text-hm-olive",
  brand: "bg-hm-brandSoft text-hm-brand",
};

const inputCls = "h-12 w-full rounded-xl border border-hm-line bg-hm-surface px-3.5 font-sans text-base text-hm-ink outline-none focus:border-hm-brand";

function LabeledInput({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-sans text-[13px] font-semibold text-hm-ink">{label}</span>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Login / Register
// ---------------------------------------------------------------------------
function AuthForm() {
  const { t } = useI18n();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.phone.trim() || f.password.length < 6 || (mode === "register" && !f.first_name.trim())) {
      return toast.error(t("required"));
    }
    setBusy(true);
    try {
      if (mode === "login") await login(f.phone.trim(), f.password);
      else await register({ first_name: f.first_name.trim(), last_name: f.last_name.trim(), phone: f.phone.trim(), email: f.email.trim() || undefined, password: f.password });
      toast.success(`${t("welcome")} !`);
    } catch (err) {
      toast.error(err?.message || t("loginError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mx-auto mt-8 w-full max-w-[440px] px-4" data-testid="auth-form">
      <div className="rounded-3xl border border-hm-line bg-hm-card p-6 shadow-lg shadow-black/5">
        <div className="mb-5 flex gap-1.5 rounded-full bg-hm-tertiary p-1">
          {["login", "register"].map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`auth-mode-${m}`}
              onClick={() => setMode(m)}
              className={`h-10 flex-1 rounded-full font-sans text-sm font-extrabold transition-colors ${mode === m ? "bg-hm-inverse text-white" : "text-hm-ink"}`}
            >
              {m === "login" ? t("login") : t("register")}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3.5">
          {mode === "register" ? (
            <div className="grid grid-cols-2 gap-3">
              <LabeledInput label={`${t("firstName")} *`} value={f.first_name} onChange={set("first_name")} testId="auth-first-name" />
              <LabeledInput label={t("lastName")} value={f.last_name} onChange={set("last_name")} testId="auth-last-name" />
            </div>
          ) : null}
          <LabeledInput label={`${t("phone")} *`} value={f.phone} onChange={set("phone")} placeholder="079 123 45 67" testId="auth-phone" />
          {mode === "register" ? <LabeledInput label={t("email")} value={f.email} onChange={set("email")} placeholder="nom@exemple.ch" testId="auth-email" /> : null}
          <div>
            <LabeledInput label={`${t("password")} *`} value={f.password} onChange={set("password")} type="password" placeholder={mode === "register" ? t("passwordHint") : undefined} testId="auth-password" />
          </div>
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="auth-submit"
          className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-hm-brand font-sans text-base font-extrabold text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50"
        >
          {mode === "login" ? <LogIn size={18} /> : <UserPlus size={18} />} {mode === "login" ? t("login") : t("register")}
        </button>
        <button type="button" data-testid="auth-switch-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-4 w-full font-sans text-sm font-bold text-hm-brand">
          {mode === "login" ? `${t("noAccountYet")} ${t("register")}` : `${t("alreadyAccount")} ${t("login")}`}
        </button>
        <p className="mt-3 text-center font-sans text-[13px] text-hm-muted">{t("guestHint")}</p>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Address editor (add / edit) — matches the Mobile validation & npa→city autofill
// ---------------------------------------------------------------------------
function AddressEditor({ existing, onClose }) {
  const { t } = useI18n();
  const { addAddress, updateAddress } = useAuth();
  const { data } = useMenu();
  const [f, setF] = useState({
    label: existing?.label ?? "",
    street: existing?.street ?? "",
    number: existing?.number ?? "",
    npa: existing?.npa ?? "",
    city: existing?.city ?? "",
    instructions: existing?.instructions ?? "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => {
    const next = { ...p, [k]: v };
    if (k === "npa") {
      const z = data?.settings?.delivery_zones?.find((zz) => zz.npa === v.trim());
      if (z && !p.city) next.city = z.city;
    }
    return next;
  });

  const submit = async () => {
    if (!f.street.trim() || !f.npa.trim() || !f.city.trim()) return toast.error(t("required"));
    setBusy(true);
    const body = { label: f.label.trim(), street: f.street.trim(), number: f.number.trim(), npa: f.npa.trim(), city: f.city.trim(), instructions: f.instructions.trim() || null };
    try {
      if (existing) await updateAddress(existing.id, body);
      else await addAddress(body);
      toast.success(t("saved"));
      onClose();
    } catch (err) {
      toast.error(err?.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-hm-brand/40 bg-hm-card p-4" data-testid="address-editor">
      <p className="mb-3 font-display text-lg text-hm-ink" data-testid="address-editor-title">{existing ? t("editAddress") : t("addAddress")}</p>
      <div className="flex flex-col gap-3">
        <LabeledInput label={t("addressLabel")} value={f.label} onChange={set("label")} testId="address-label" />
        <div className="grid grid-cols-[3fr_1fr] gap-3">
          <LabeledInput label={`${t("street")} *`} value={f.street} onChange={set("street")} testId="address-street" />
          <LabeledInput label={t("number")} value={f.number} onChange={set("number")} testId="address-number" />
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-3">
          <LabeledInput label={`${t("npa")} *`} value={f.npa} onChange={set("npa")} testId="address-npa" />
          <LabeledInput label={`${t("city")} *`} value={f.city} onChange={set("city")} testId="address-city" />
        </div>
        <LabeledInput label={t("deliveryInstructions")} value={f.instructions} onChange={set("instructions")} testId="address-instructions" />
      </div>
      <div className="mt-4 flex gap-2.5">
        <button type="button" data-testid="address-cancel" onClick={onClose} className="h-11 flex-1 rounded-full border-[1.5px] border-hm-lineStrong font-sans font-bold text-hm-ink">
          {t("cancel")}
        </button>
        <button type="button" data-testid="address-save" onClick={submit} disabled={busy} className="flex h-11 flex-[1.4] items-center justify-center gap-2 rounded-full bg-hm-brand font-sans font-extrabold text-white disabled:opacity-50">
          <Check size={16} /> {t("save")}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile: details (edit), marketing, addresses (CRUD), order history
// ---------------------------------------------------------------------------
function Profile() {
  const navigate = useNavigate();
  const { t, tx } = useI18n();
  const { user, logout, updateProfile, deleteAddress } = useAuth();
  const { data: orders } = useMyOrders(true);

  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ first_name: user.first_name, last_name: user.last_name, phone: user.phone, email: user.email || "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [addrForm, setAddrForm] = useState(null); // null | {} (new) | address (edit)

  const saveProfile = async () => {
    if (!f.first_name.trim() || !f.phone.trim()) return toast.error(t("required"));
    setBusy(true);
    try {
      await updateProfile({ first_name: f.first_name.trim(), last_name: f.last_name.trim(), phone: f.phone.trim(), email: f.email.trim() || undefined });
      toast.success(t("saved"));
      setEdit(false);
    } catch (err) {
      toast.error(err?.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const removeAddress = async (a) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await deleteAddress(a.id);
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err?.message || "Erreur");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[760px] px-4 py-6">
      {/* Profile header */}
      <div className="mb-2 flex items-center justify-between">
        <p className="font-sans text-xs font-bold uppercase tracking-wide text-hm-muted">{t("loggedInAs")}</p>
        <button type="button" data-testid="profile-edit-toggle" onClick={() => setEdit((v) => !v)} className="font-sans text-sm font-bold text-hm-brand">
          {edit ? t("close") : t("edit")}
        </button>
      </div>
      <div className="rounded-3xl border border-hm-line bg-hm-card p-5 shadow-lg shadow-black/5">
        {edit ? (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <LabeledInput label={`${t("firstName")} *`} value={f.first_name} onChange={set("first_name")} testId="profile-first-name" />
              <LabeledInput label={t("lastName")} value={f.last_name} onChange={set("last_name")} testId="profile-last-name" />
            </div>
            <LabeledInput label={`${t("phone")} *`} value={f.phone} onChange={set("phone")} testId="profile-phone" />
            <LabeledInput label={t("email")} value={f.email} onChange={set("email")} testId="profile-email" />
            <button type="button" data-testid="profile-save" onClick={saveProfile} disabled={busy} className="flex h-12 items-center justify-center gap-2 rounded-full bg-hm-brand font-sans font-extrabold text-white disabled:opacity-50">
              <Check size={16} /> {t("save")}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-hm-brandSoft text-hm-brand"><User size={26} /></span>
            <div>
              <p className="font-display text-2xl text-hm-ink" data-testid="profile-name">{user.first_name} {user.last_name}</p>
              <p className="font-sans text-sm text-hm-muted" data-testid="profile-phone-text">{user.phone}</p>
              {user.email ? <p className="font-sans text-sm text-hm-muted" data-testid="profile-email-text">{user.email}</p> : null}
            </div>
          </div>
        )}
      </div>

      {/* Saved addresses */}
      <div className="mt-8 mb-3 flex items-center justify-between">
        <h2 className="font-display text-2xl text-hm-ink">{t("savedAddresses")}</h2>
        {!addrForm ? (
          <button type="button" data-testid="address-add" onClick={() => setAddrForm({})} className="flex items-center gap-1.5 font-sans text-sm font-bold text-hm-brand">
            <Plus size={16} /> {t("addAddress")}
          </button>
        ) : null}
      </div>

      {addrForm ? (
        <div className="mb-3">
          <AddressEditor existing={addrForm.id ? addrForm : null} onClose={() => setAddrForm(null)} />
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-hm-line bg-hm-card" data-testid="saved-addresses">
        {user.addresses.length === 0 ? (
          <p className="p-5 font-sans text-sm text-hm-muted" data-testid="no-addresses">{t("noAddresses")}</p>
        ) : (
          user.addresses.map((a, i) => (
            <div key={a.id} className={`flex items-start gap-3 p-4 ${i > 0 ? "border-t border-hm-line" : ""}`} data-testid={`address-row-${a.id}`}>
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-hm-brandSoft text-hm-brand"><MapPin size={16} /></span>
              <div className="flex-1">
                {a.label ? <p className="font-sans text-sm font-bold text-hm-ink">{a.label}</p> : null}
                <p className="font-sans text-sm text-hm-muted">{a.street} {a.number}, {a.npa} {a.city}</p>
                {a.instructions ? <p className="font-sans text-[13px] italic text-hm-muted">{a.instructions}</p> : null}
              </div>
              <button type="button" data-testid={`address-edit-${a.id}`} onClick={() => setAddrForm(a)} className="flex h-9 w-9 items-center justify-center rounded-full bg-hm-tertiary text-hm-ink"><Pencil size={15} /></button>
              <button type="button" data-testid={`address-delete-${a.id}`} onClick={() => removeAddress(a)} className="flex h-9 w-9 items-center justify-center rounded-full bg-hm-tertiary text-hm-error"><Trash2 size={15} /></button>
            </div>
          ))
        )}
      </div>

      {/* Order history */}
      <h2 className="mt-8 mb-3 font-display text-2xl text-hm-ink">{t("orderHistory")}</h2>
      <div className="flex flex-col gap-3" data-testid="order-history">
        {!orders || orders.length === 0 ? (
          <div className="rounded-2xl border border-hm-line bg-hm-card p-5">
            <p className="font-sans text-sm text-hm-muted" data-testid="no-history">{t("noHistory")}</p>
          </div>
        ) : (
          orders.map((o) => (
            <div key={o.id} className="rounded-2xl border border-hm-line bg-hm-card p-4" data-testid={`history-order-${o.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-display text-xl text-hm-ink">#{o.order_number}</span>
                <span className={`rounded-full px-2.5 py-1 font-sans text-[11px] font-extrabold ${TONE[statusTone(o.status)]}`} data-testid="order-status-badge">
                  {statusLabel(o.status, o.type, t)}
                </span>
              </div>
              <p className="mt-0.5 font-sans text-[13px] text-hm-muted">{o.type === "pickup" ? t("pickup") : t("delivery")} · {fmtTime(o.created_at)}</p>
              <p className="mt-1 line-clamp-2 font-sans text-sm text-hm-ink">{o.items.map((i) => `${i.quantity}× ${tx(i.name)}`).join(", ")}</p>
              <p className="mt-1.5 font-sans text-[15px] font-extrabold text-hm-ink">{chf(o.total)}</p>
            </div>
          ))
        )}
      </div>

      <div className="mt-8 flex gap-2.5">
        <button type="button" data-testid="account-browse-menu" onClick={() => navigate("/")} className="flex-1 rounded-full border-[1.5px] border-hm-lineStrong py-3 font-sans font-bold text-hm-ink transition-colors hover:bg-hm-tertiary">
          {t("browseMenu")}
        </button>
        <button type="button" data-testid="logout-button" onClick={() => { logout(); toast.success(t("logout")); }} className="flex items-center justify-center gap-2 rounded-full border-[1.5px] border-hm-lineStrong px-5 py-3 font-sans font-bold text-hm-ink transition-colors hover:bg-hm-tertiary">
          <LogOut size={16} /> {t("logout")}
        </button>
      </div>
    </div>
  );
}

export default function AccountPage() {
  const { ready, user } = useAuth();
  return (
    <div className="min-h-screen bg-hm-surface">
      <Header />
      {!ready ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-hm-line border-t-hm-brand" />
        </div>
      ) : user ? (
        <Profile />
      ) : (
        <AuthForm />
      )}
    </div>
  );
}
